import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMaps } from "../../../config/google-maps-loader";
import { MapPin, Clock, User, ArrowLeft, CheckCircle, XCircle, AlertCircle, Navigation, MessageCircle, Play, Ban } from "lucide-react";
import toast from "react-hot-toast";
import Chat from "./chat";
import CompleteJobDialog from "../modals/complete-job-dialog";
import JobApprovalDialog from "../modals/job-approval-dialog";
import CantStartDialog from "../modals/cant-start-dialog";
import { getTaskDetails, acceptTask, makeCounterOffer, rejectTask, transformTaskData } from "../../../services/scheduled-tasks-api";
import { useSocket } from "../../../context/socket-context";
import ActiveJobTracking from "../tasks/active-job-tracking";

const ScheduleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [isOngoingTask, setIsOngoingTask] = useState(false);

  // API State
  const [loading, setLoading] = useState(true);
  const [taskError, setTaskError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [appointmentData, setAppointmentData] = useState(null); // Will be populated from API

  // Socket Context
  const { scheduledTaskUpdates } = useSocket();

  // UI State
  const { isLoaded } = useGoogleMaps();
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("waiting");
  const [showCantStartDialog, setShowCantStartDialog] = useState(false);

  // Form State
  const [counterOfferAmount, setCounterOfferAmount] = useState("");
  const [counterOfferReason, setCounterOfferReason] = useState("");
  const [showCounterOfferDialog, setShowCounterOfferDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Get user data from localStorage
  const authUser = localStorage.getItem("authUser")
    ? JSON.parse(localStorage.getItem("authUser"))
    : null;
  const token = localStorage.getItem("authToken");

  // Fetch appointment details
  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      setLoading(true);
      setTaskError(null);

      try {
        // Check local storage for ongoing tasks first (legacy support & instant load)
        const storedSchedule = JSON.parse(localStorage.getItem('handymanSchedule') || '[]');
        const ongoingTask = storedSchedule.find((task) => task.serviceRequestId === id);

        if (ongoingTask) {
          setIsOngoingTask(true);
        }

        // Call API to get task details
        const result = await getTaskDetails(id);

        if (result.success) {
          console.log('✅ Fetched task details:', result.task);
          const transformed = transformTaskData(result.task);
          setAppointmentData(transformed);
        } else {
          // If API fails but we have local data, use that as fallback
          if (ongoingTask) {
            console.warn('⚠️ API failed, using local data fallback');
            setAppointmentData(transformLocalTask(ongoingTask));
          } else {
            throw new Error(result.error || 'Failed to fetch task details');
          }
        }
      } catch (error) {
        console.error("Error fetching appointment details:", error);
        setTaskError(error.message);
        toast.error("Failed to load appointment details");
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentDetails();
  }, [id, token]);

  // Helper to transform local storage task to component format
  const transformLocalTask = (ongoingTask) => {
    return {
      id: ongoingTask.serviceRequestId,
      date: ongoingTask.scheduleDate || new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      time: ongoingTask.scheduleTime || new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      customer: {
        name: ongoingTask.customerName || "Customer",
        phone: ongoingTask.customerPhone || "+92 300 1234567",
        email: "customer@example.com",
        avatar: ongoingTask.customerAvatar || "/static/media/default-avatar.png",
        rating: 4.7,
      },
      service: {
        title: ongoingTask.jobTitle || "Service Request",
        category: ongoingTask.subcategory?.split(", ")[0] || "General",
        subcategory: ongoingTask.subcategory || "Service",
        description: ongoingTask.description || "No description provided",
        notes: "",
      },
      location: {
        address: ongoingTask.address || "Address not provided",
        coordinates: ongoingTask.coordinates || [31.4782, 74.3140],
        fullAddress: ongoingTask.address || "Address not provided",
      },
      workDetails: {
        basePrice: ongoingTask.finalPrice || ongoingTask.basePrice || 2500,
        estimatedTime: "2-3 hours",
        status: ongoingTask.workStatus || ongoingTask.status || "ongoing",
        scheduledDate: ongoingTask.scheduleDate || null,
        scheduledTime: ongoingTask.scheduleTime || null,
      },
      handyman: {
        name: "Your Name",
        phone: "+92 300 9876543",
        rating: 4.8,
        completedJobs: 127,
      },
    };
  };

  // Listen for real-time updates for this task
  useEffect(() => {
    if (scheduledTaskUpdates && scheduledTaskUpdates.data?.taskId === id) {
      console.log('🔔 Task update for current task:', scheduledTaskUpdates);

      // Refresh task details
      const refreshTask = async () => {
        const result = await getTaskDetails(id);
        if (result.success) {
          const transformed = transformTaskData(result.task);
          setAppointmentData(transformed);
        }
      };

      refreshTask();
    }
  }, [scheduledTaskUpdates, id]);

  // ==================== MAP MARKER SETUP ====================
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google?.maps || !appointmentData?.location?.coordinates || loading) return;

    const map = mapRef.current;
    // Coordinates are stored as [lng, lat] (GeoJSON format)
    const [lng, lat] = appointmentData.location.coordinates;
    const position = { lat, lng };

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker with custom icon
    markerRef.current = new window.google.maps.Marker({
      position: position,
      map: map,
      title: appointmentData.customer.name,
      icon: {
        url: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
        scaledSize: new window.google.maps.Size(30, 30),
      },
    });

    // Create info window
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div class="p-2">
          <p class="font-semibold text-gray-900">${appointmentData.customer.name}</p>
          <p class="text-sm text-gray-600">${appointmentData.location.address}</p>
          <p class="text-xs text-gray-500 mt-1">${appointmentData.service.title}</p>
        </div>
      `,
    });

    markerRef.current.addListener("click", () => {
      infoWindow.open(map, markerRef.current);
    });

    // Open info window by default
    infoWindow.open(map, markerRef.current);

    // Center map on marker
    map.setCenter(position);
    map.setZoom(15);
  }, [isLoaded, appointmentData, loading]);

  // ==================== ACTIONS ====================

  const handleAcceptTask = async () => {
    setUpdatingStatus(true);
    try {
      // Use ID from URL params as primary source of truth, fallback to appointmentData
      const taskId = id || appointmentData.id || appointmentData.serviceRequestId;

      if (!taskId) {
        throw new Error("Task ID is missing");
      }

      // If we have an offer ID, use it. Otherwise assume task ID.
      const offerId = appointmentData.myOffer?.id || appointmentData.myOffer?._id || taskId;
      const acceptedPrice = appointmentData.workDetails.basePrice || 0;
      const message = "I accept this task at the offered price.";

      const result = await acceptTask(taskId, offerId, acceptedPrice, message);

      if (result.success) {
        toast.success('Task accepted successfully!');
        // Refresh details
        const updated = await getTaskDetails(taskId);
        if (updated.success) {
          setAppointmentData(transformTaskData(updated.task));
        }
      } else {
        throw new Error(result.error || 'Failed to accept task');
      }
    } catch (error) {
      console.error('Error accepting task:', error);
      toast.error(error.message || 'Failed to accept task');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCounterOffer = async () => {
    if (!counterOfferAmount) {
      toast.error("Please enter a counter offer amount");
      return;
    }

    setUpdatingStatus(true);
    try {
      const taskId = appointmentData.id;
      const offerId = appointmentData.id;

      const result = await makeCounterOffer(taskId, offerId, counterOfferAmount, counterOfferReason);

      if (result.success) {
        toast.success('Counter offer sent successfully!');
        setShowCounterOfferDialog(false);
        // Refresh details
        const updated = await getTaskDetails(id);
        if (updated.success) {
          setAppointmentData(transformTaskData(updated.task));
        }
      } else {
        throw new Error(result.error || 'Failed to make counter offer');
      }
    } catch (error) {
      console.error('Error making counter offer:', error);
      toast.error(error.message || 'Failed to make counter offer');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleRejectTask = async () => {
    setUpdatingStatus(true);
    try {
      const taskId = appointmentData.id;
      const offerId = appointmentData.id;

      const result = await rejectTask(taskId, offerId, rejectReason || "No reason provided", "other");

      if (result.success) {
        toast.success('Task rejected successfully');
        setShowRejectDialog(false);
        navigate('/handyman/schedule');
      } else {
        throw new Error(result.error || 'Failed to reject task');
      }
    } catch (error) {
      console.error('Error rejecting task:', error);
      toast.error(error.message || 'Failed to reject task');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ==================== STATUS UPDATE ====================
  const handleStatusUpdate = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      // NOTE: Status update endpoints were not provided in the requirements.
      // This is a placeholder that simulates the update or could be connected 
      // to a future endpoint like PATCH /scheduled-tasks/:id/status

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      setAppointmentData((prev) => ({
        ...prev,
        workDetails: {
          ...prev.workDetails,
          status: newStatus,
        },
      }));

      // Update local storage if needed
      if (isOngoingTask) {
        const storedSchedule = JSON.parse(localStorage.getItem('handymanSchedule') || '[]');
        const updatedSchedule = storedSchedule.map((task) =>
          task.serviceRequestId === id
            ? { ...task, workStatus: newStatus, status: newStatus }
            : task
        );
        localStorage.setItem('handymanSchedule', JSON.stringify(updatedSchedule));
      }

      const statusMessages = {
        started: "Work started successfully!",
        in_progress: "Work started successfully!",
        completed: "Work marked as completed!",
        cancelled: "Appointment cancelled.",
      };

      toast.success(statusMessages[newStatus] || "Status updated successfully!");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.message || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ==================== HANDLE START TASK ====================
  const handleStartTask = () => {
    handleStatusUpdate("started");
  };

  // ==================== HANDLE COMPLETE JOB ====================
  const handleCompleteJob = (data) => {
    // Close the complete job dialog first
    setShowCompleteDialog(false);

    // Simulate approval process - randomly set approval status after a short delay
    // This ensures the first dialog closes before the second one opens
    setTimeout(() => {
      const statuses = ["waiting", "approved", "notApproved"];
      const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
      setApprovalStatus(randomStatus);
      setShowApprovalDialog(true);

      // Update status to completed
      handleStatusUpdate("completed");
    }, 500);
  };

  // ==================== HANDLE CAN'T START ====================
  const handleCantStart = (data) => {
    setShowCantStartDialog(false);
    toast.success("Your reason has been submitted. Support will contact you soon.");
    // Update status to cancelled
    handleStatusUpdate("cancelled");
  };

  // ==================== OPEN DIRECTIONS ====================
  const handleOpenDirections = () => {
    const [lat, lng] = appointmentData.location.coordinates;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, "_blank");
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
          <p className="text-gray-600 text-sm font-medium">Loading appointment details...</p>
        </div>
      </div>
    );
  }

  const statusConfig = {
    pending_offers: {
      label: "Pending Offers",
      color: "bg-orange-100 text-orange-700 border-orange-200",
      icon: AlertCircle,
    },
    scheduled: {
      label: "Scheduled",
      color: "bg-blue-100 text-blue-700 border-blue-200",
      icon: Clock,
    },
    ongoing: {
      label: "Ongoing",
      color: "bg-orange-100 text-orange-700 border-orange-200",
      icon: AlertCircle,
    },
    current: {
      label: "Current",
      color: "bg-orange-100 text-orange-700 border-orange-200",
      icon: AlertCircle,
    },
    started: {
      label: "Started",
      color: "bg-yellow-100 text-yellow-700 border-yellow-200",
      icon: Play,
    },
    in_progress: {
      label: "In Progress",
      color: "bg-yellow-100 text-yellow-700 border-yellow-200",
      icon: AlertCircle,
    },
    completed: {
      label: "Completed",
      color: "bg-green-100 text-green-700 border-green-200",
      icon: CheckCircle,
    },
    cancelled: {
      label: "Cancelled",
      color: "bg-red-100 text-red-700 border-red-200",
      icon: XCircle,
    },
  };

  const currentStatus = statusConfig[appointmentData.workDetails.status] || statusConfig.scheduled;
  const StatusIcon = currentStatus.icon;

  const activeStatuses = [
    "assigned",
    "on_the_way",
    "arrived",
    "work_started",
    "in_progress",
    "approved",
    "payment_received",
    "completed"
  ];

  // If the job is active/assigned, render the ActiveJobTracking component
  if (!loading && appointmentData && activeStatuses.includes(appointmentData.workDetails.status)) {
    // Map appointmentData back to the structure ActiveJobTracking expects
    const jobForTracking = {
      _id: id || appointmentData.id,
      serviceRequestId: appointmentData.serviceRequestId || id,
      status: appointmentData.workDetails.status,
      customerName: appointmentData.customer.name,
      customerPhone: appointmentData.customer.phone,
      customerRating: appointmentData.customer.rating,
      serviceDetails: {
        location: {
          address: appointmentData.location.address,
          coordinates: appointmentData.location.coordinates
        },
        subServices: appointmentData.service.subServices || []
      },
      mainService: appointmentData.service.title,
      finalPrice: appointmentData.workDetails.finalPrice || appointmentData.workDetails.basePrice,
      customer: { _id: appointmentData.customer.id || "cust_unknown" }
    };

    return (
      <ActiveJobTracking
        job={jobForTracking}
        onBack={() => navigate('/handyman/schedule')}
        onJobComplete={() => {
          toast.success("Job completed!");
          navigate('/handyman/my-jobs');
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Matching Schedule page style */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm dark:bg-slate-900 dark:border-slate-700 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/handyman/schedule")}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors dark:border-slate-700 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Schedule Details</h1>
              <p className="text-gray-600 mt-1 dark:text-gray-300">{appointmentData.date} · {appointmentData.time}</p>
            </div>
          </div>
          <div className="ml-auto">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${currentStatus.color}`}>
              <StatusIcon className="h-4 w-4" />
              <span className="text-sm font-semibold">{currentStatus.label}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="h-[400px] relative bg-gray-100">
          {!isLoaded ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm z-50">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
                <p className="text-gray-600 text-sm font-medium">Loading map...</p>
              </div>
            </div>
          ) : appointmentData?.location?.coordinates ? (
            <GoogleMap
              onLoad={(map) => (mapRef.current = map)}
              center={{
                lat: appointmentData.location.coordinates[1],
                lng: appointmentData.location.coordinates[0],
              }}
              zoom={15}
              mapContainerStyle={{ width: "100%", height: "100%" }}
              options={{
                mapId: "SCHEDULE_MAP",
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <p className="text-gray-500 text-sm">No location data available</p>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-[#EFA938]" />
              <div>
                <p className="font-semibold text-gray-900">{appointmentData.location.address}</p>
                <p className="text-sm text-gray-600">{appointmentData.location.fullAddress}</p>
              </div>
            </div>
            <button
              onClick={handleOpenDirections}
              className="px-4 py-2 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors flex items-center gap-2"
            >
              <Navigation className="h-4 w-4" />
              Directions
            </button>
          </div>
        </div>
      </div>

      {/* Customer & Work Details */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Customer Details Card */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="h-5 w-5 text-[#EFA938]" />
              Customer Details
            </h2>
          </div>
          <div className="px-6 py-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-[#EFA938] to-orange-400 flex items-center justify-center text-white text-2xl font-bold shadow-lg ring-4 ring-amber-100">
                  {appointmentData.customer.name.charAt(0)}
                </div>
                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-green-500 border-2 border-white shadow-sm"></div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">{appointmentData.customer.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-lg">
                        {i < Math.floor(appointmentData.customer.rating) ? "★" : "☆"}
                      </span>
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-600">({appointmentData.customer.rating})</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 space-y-3">
              {!isChatVisible ? (
                <button
                  onClick={() => setIsChatVisible(true)}
                  className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl bg-gradient-to-r from-[#EFA938] to-orange-400 text-white font-semibold hover:from-[#d78c1f] hover:to-orange-500 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span>Start Chat</span>
                </button>
              ) : (
                <div className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl bg-green-50 border-2 border-green-200">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-sm font-semibold text-green-700">Chat Active</span>
                </div>
              )}
              <p className="text-xs text-gray-500 text-center leading-relaxed">
                <span className="font-medium">Privacy Protected:</span> Use the chat feature to communicate securely. Customer contact information is kept private.
              </p>
            </div>
          </div>
        </div>

        {/* Work Details Card */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="h-5 w-5 text-[#EFA938]" />
              Work Details
            </h2>
          </div>
          <div className="px-6 py-6 space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{appointmentData.service.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{appointmentData.service.category} · {appointmentData.service.subcategory}</p>
            </div>
            <div>
              <p className="text-gray-700 leading-relaxed">{appointmentData.service.description}</p>
            </div>
            {appointmentData.service.notes && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm font-medium text-amber-900">Notes:</p>
                <p className="text-sm text-amber-700 mt-1">{appointmentData.service.notes}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Price</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">PKR {appointmentData.workDetails.basePrice.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Duration</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{appointmentData.workDetails.estimatedTime}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Handyman Details & Status Actions */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Your Details & Actions</h2>
        </div>
        <div className="px-6 py-6">
          <div className="grid md:grid-cols-2 gap-6 items-start">
            {/* Handyman Info */}
            <div className="space-y-10">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {appointmentData.handyman.name || authUser?.name || "Your Name"}
                </h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span>⭐ {appointmentData.handyman.rating || authUser?.rating || "New"}</span>
                  <span>✓ {appointmentData.handyman.completedJobs || authUser?.completedJobs || 0} jobs completed</span>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Phone:</strong> {appointmentData.handyman.phone || authUser?.phoneNumber || authUser?.phone || "Not set"}
                </p>
              </div>
            </div>

            {/* Status Update Buttons */}
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700">Update Work Status:</p>
                {/* Debug info - remove in production */}
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  Status: {appointmentData.workDetails.status}
                </span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                {/* For pending tasks - show Accept/Reject/Counter buttons */}
                {(appointmentData.workDetails.status === "pending" || appointmentData.workDetails.status === "offered" || appointmentData.workDetails.status === "pending_offers") && (
                  <>
                    {/* Check if I already made an offer */}
                    {appointmentData.myOffer && appointmentData.myOffer.status === 'pending' ? (
                      <div className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 flex items-center justify-center gap-2">
                        <Clock className="h-5 w-5" />
                        <span className="font-semibold">Offer Sent - Waiting for Approval</span>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={handleAcceptTask}
                          disabled={updatingStatus}
                          className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {updatingStatus ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          ) : (
                            <>
                              <CheckCircle className="h-5 w-5 flex-shrink-0" />
                              <span>Accept Task</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setShowCounterOfferDialog(true)}
                          disabled={updatingStatus}
                          className="w-full min-h-[48px] px-4 py-3 rounded-lg border border-[#EFA938] text-[#EFA938] font-semibold hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <span>Make Counter Offer</span>
                        </button>
                        <button
                          onClick={() => setShowRejectDialog(true)}
                          disabled={updatingStatus}
                          className="w-full min-h-[48px] px-4 py-3 rounded-lg border border-red-300 text-red-600 font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <XCircle className="h-5 w-5 flex-shrink-0" />
                          <span>Reject Task</span>
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* For ongoing/current tasks - show Start Task and Can't Start buttons */}
                {(appointmentData.workDetails.status === "ongoing" || appointmentData.workDetails.status === "current") && (
                  <>
                    <button
                      onClick={handleStartTask}
                      disabled={updatingStatus}
                      className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {updatingStatus ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      ) : (
                        <>
                          <Play className="h-5 w-5 flex-shrink-0" />
                          <span>Start Task</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowCantStartDialog(true)}
                      disabled={updatingStatus}
                      className="w-full min-h-[48px] px-4 py-3 rounded-lg border-2 border-red-300 text-red-600 font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Ban className="h-5 w-5 flex-shrink-0" />
                      <span>Can't Start Work</span>
                    </button>
                  </>
                )}

                {/* For started status - show Complete Job button only */}
                {appointmentData.workDetails.status === "started" && (
                  <button
                    onClick={() => setShowCompleteDialog(true)}
                    disabled={updatingStatus}
                    className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span>Complete Job</span>
                  </button>
                )}

                {/* For regular scheduled appointments */}
                {appointmentData.workDetails.status === "scheduled" && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate("in_progress")}
                      disabled={updatingStatus}
                      className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-yellow-500 text-white font-semibold hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {updatingStatus ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      ) : (
                        <>
                          <AlertCircle className="h-5 w-5 flex-shrink-0" />
                          <span>Start Work</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate("cancelled")}
                      disabled={updatingStatus}
                      className="w-full min-h-[48px] px-4 py-3 rounded-lg border border-red-300 text-red-600 font-semibold hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {updatingStatus ? (
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                      ) : (
                        <>
                          <XCircle className="h-5 w-5 flex-shrink-0" />
                          <span>Cancel Appointment</span>
                        </>
                      )}
                    </button>
                  </>
                )}

                {appointmentData.workDetails.status === "in_progress" && (
                  <button
                    onClick={() => handleStatusUpdate("completed")}
                    disabled={updatingStatus}
                    className="w-full min-h-[48px] px-4 py-3 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {updatingStatus ? (
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 flex-shrink-0" />
                        <span>Mark as Completed</span>
                      </>
                    )}
                  </button>
                )}

                {appointmentData.workDetails.status === "completed" && (
                  <div className="min-h-[48px] p-4 rounded-lg bg-green-50 border border-green-200 flex items-center">
                    <p className="text-sm font-medium text-green-800 flex items-center gap-2 w-full">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span>Work completed successfully!</span>
                    </p>
                  </div>
                )}
                {appointmentData.workDetails.status === "cancelled" && (
                  <div className="min-h-[48px] p-4 rounded-lg bg-red-50 border border-red-200 flex items-center">
                    <p className="text-sm font-medium text-red-800 flex items-center gap-2 w-full">
                      <XCircle className="h-5 w-5 flex-shrink-0" />
                      <span>This appointment has been cancelled.</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Component */}
      {isChatVisible && (
        <Chat
          customerName={appointmentData.customer.name}
          customerAvatar={appointmentData.customer.avatar}
          isLive={true}
          onEndChat={() => {
            setIsChatVisible(false);
          }}
        />
      )}

      {/* Complete Job Dialog */}
      {showCompleteDialog && (
        <CompleteJobDialog
          onClose={() => setShowCompleteDialog(false)}
          onSubmit={handleCompleteJob}
        />
      )}

      {/* Job Approval Dialog */}
      {showApprovalDialog && (
        <JobApprovalDialog
          onClose={() => setShowApprovalDialog(false)}
          status={approvalStatus}
        />
      )}

      {/* Can't Start Dialog */}
      {showCantStartDialog && (
        <CantStartDialog
          onClose={() => setShowCantStartDialog(false)}
          onSubmit={handleCantStart}
        />
      )}

      {/* Counter Offer Modal */}
      {showCounterOfferDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Make Counter Offer</h3>
              <button
                onClick={() => setShowCounterOfferDialog(false)}
                className="text-gray-400 hover:text-gray-500 transition-colors"
                disabled={updatingStatus}
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Amount (PKR)</label>
                <input
                  type="number"
                  value={counterOfferAmount}
                  onChange={(e) => setCounterOfferAmount(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#EFA938] focus:border-transparent outline-none transition-all"
                  placeholder="Enter amount"
                  disabled={updatingStatus}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={counterOfferReason}
                  onChange={(e) => setCounterOfferReason(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#EFA938] focus:border-transparent outline-none transition-all resize-none h-24"
                  placeholder="Why are you making this counter offer?"
                  disabled={updatingStatus}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowCounterOfferDialog(false)}
                  disabled={updatingStatus}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCounterOffer}
                  disabled={!counterOfferAmount || updatingStatus}
                  className="flex-1 px-4 py-2 rounded-lg bg-[#EFA938] text-white font-medium hover:bg-[#d78c1f] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {updatingStatus ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Sending...</span>
                    </>
                  ) : (
                    'Send Offer'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Request Modal */}
      {showRejectDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Reject Task</h3>
              <button
                onClick={() => setShowRejectDialog(false)}
                className="text-gray-400 hover:text-gray-500 transition-colors"
                disabled={updatingStatus}
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-100 text-red-700 text-sm">
                Are you sure you want to reject this task? This action cannot be undone.
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Rejection</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all resize-none h-24"
                  placeholder="Please provide a reason..."
                  disabled={updatingStatus}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowRejectDialog(false)}
                  disabled={updatingStatus}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectTask}
                  disabled={!rejectReason || updatingStatus}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {updatingStatus ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Rejecting...</span>
                    </>
                  ) : (
                    'Reject Task'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleDetail;

